import { useEffect, useState, useRef, Fragment } from "react";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { fileUrl, useDash } from "./ctx";
import { useNavigate } from "react-router-dom";
import { copilotAskUrl, conversationReplyPrompt } from "@/lib/copilotLink";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, Send, CheckCircle2, ShieldAlert, User, MessagesSquare, Headset, Bot,
  Paperclip, Smile, MessageSquareText, Archive, ArchiveRestore, X, Download, BadgeCheck, Info, Mail, Home, Users, Mic, Scale, Phone, UserPlus, Trash2, RotateCcw, ShieldCheck, Sparkles, FileDown, Globe, Copy, ClipboardList, Printer, Maximize2, Minimize2 } from "lucide-react";
import { VoiceCallTranscript } from "../../components/VoiceCallTranscript";
import { DocumentPicker } from "../../components/DocumentPicker";
import { EmergencyConvBanner } from "../../components/EmergencyRecord";
import { V } from "@/lib/vocab";

const EMOJIS = ["😀","😊","👍","🙏","👋","🎉","✅","❤️","😂","🙌","👏","🤝","💪","🔥","⭐","💯",
  "😅","🤔","😍","😎","🥳","😢","😡","🚗","🏠","🔧","📦","📅","⏰","📞","📍","⚠️","🛠️","🔑","💧","🅿️","🐾","🌳","☀️","🌧️"];

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

// Audit helpers — who said what, when. One line per message; the same text a compliance file wants.
const speakerOf = (m, personWord) => m.direction === "inbound" ? personWord : (m.is_human ? (m.sent_by || "Staff") : "AI concierge");
const transcriptLine = (m, personWord) => `[${fmtTime(m.created_at)}] ${speakerOf(m, personWord)}: ${(m.body || "").trim()}`;
async function copyText(text, okMsg) {
  try { await navigator.clipboard.writeText(text); toast.success(okMsg); }
  catch { toast.error("Couldn't copy — your browser blocked clipboard access"); }
}

// Shared pressable styling for the conversation header controls (color + a subtle press).
const HDR_ICON_BTN = "shrink-0 h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center transition active:scale-90";
const HDR_PILL_BTN = "shrink-0 h-7 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center gap-1.5 px-2.5 text-[11px] font-semibold transition active:scale-95";

const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Best-effort masking of gate codes / door PINs so a printed record can safely leave the building.
const SECRET_RE = /\b(gate code|door code|access code|lock code|pin code|entry code|passcode|password|combination|combo|pin|code)\b(\s*(?:is|are|=|:|->|–|—|-)?\s*)([A-Za-z0-9][A-Za-z0-9\-]{1,9})/gi;
const looksSecret = (v) => { const s = (v || "").trim(); return /\d/.test(s) || /^[A-Z0-9]{3,8}$/.test(s); };
const maskSecrets = (t) => String(t || "").replace(SECRET_RE, (full, kw, sep, val) => (looksSecret(val) ? `${kw}${sep}••••` : full));

function MediaBubble({ item, onImage }) {
  const url = fileUrl(item.path);
  if (item.kind === "image") {
    return <img src={url} alt="" onClick={() => onImage(url)} className="rounded-lg mb-1.5 max-h-48 cursor-zoom-in hover:opacity-90 transition-opacity" data-testid="media-image" />;
  }
  if (item.kind === "audio") {
    return <audio controls src={url} className="mb-1.5 max-w-[240px]" data-testid="media-audio" />;
  }
  if (item.kind === "video") {
    return <video controls src={url} className="rounded-lg mb-1.5 max-h-56 max-w-full" data-testid="media-video" />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="media-file"
      className="flex items-center gap-2 mb-1.5 rounded-lg bg-black/5 px-3 py-2 text-xs font-medium hover:bg-black/10 transition-colors">
      <FileText className="h-4 w-4 shrink-0" /> <span className="truncate max-w-[180px]">{item.filename || "Attachment"}</span> <Download className="h-3.5 w-3.5 ml-auto" />
    </a>
  );
}

export default function ConversationPanel({ convId, role, onChanged, expanded, onToggleExpand }) {
  const { tenant: dashTenant } = useDash() || {};
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [showDocs, setShowDocs] = useState(false);          // "Send a document" picker
  const [pendingDoc, setPendingDoc] = useState(null);        // { id, title } whose link sits in the reply box
  const [canned, setCanned] = useState([]);
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showResident, setShowResident] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffTo, setHandoffTo] = useState("");
  const [handoffNote, setHandoffNote] = useState("");
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const baseTextRef = useRef("");
  const speechSupported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const readOnly = role === "board_member";
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastTypingRef = useRef(0);

  const toggleDictation = () => {
    if (!speechSupported) return;
    if (listening) { try { recogRef.current?.stop(); } catch (e) {} return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = navigator.language || "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    baseTextRef.current = reply ? reply.trim() + " " : "";
    rec.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      setReply(baseTextRef.current + txt);
      notifyTyping();
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") toast.error("Microphone permission denied.");
    };
    rec.onend = () => setListening(false);
    recogRef.current = rec;
    try { rec.start(); setListening(true); } catch (e) { setListening(false); }
  };
  useEffect(() => () => { try { recogRef.current?.stop(); } catch (e) {} }, []);

  const load = () => convId && api.get(`/conversations/${convId}`).then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); setShowEmoji(false); setShowCanned(false); load(); }, [convId]);

  useEffect(() => {
    if (!convId) return;
    const t = setInterval(() => { api.get(`/conversations/${convId}`).then((r) => setData(r.data)).catch(() => {}); }, 4000);
    return () => clearInterval(t);
  }, [convId]);

  const tenantId = data?.conversation?.tenant_id;
  const bubbles = data?.transcript_bubbles !== false;
  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/canned`).then((r) => setCanned(r.data)).catch(() => setCanned([]));
    api.get(`/tenants/${tenantId}/assignees`).then((r) => setAssignees(r.data)).catch(() => setAssignees([]));
  }, [tenantId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.conversation?.id, data?.messages?.length]);

  if (!convId) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-8">
        <MessagesSquare className="h-10 w-10 text-sand-300 mb-3" strokeWidth={1.25} />
        <p className="font-heading font-bold text-ink">Select a conversation</p>
        <p className="text-sm mt-1">Pick a thread on the left to read it, with the sources behind each answer.</p>
      </div>
    );
  }

  const conv = data?.conversation;
  const resident = data?.resident || {};
  const isWhatsApp = conv?.channel === "whatsapp";
  const channelLabel = conv?.channel === "voice" ? "Voice" : isWhatsApp ? "WhatsApp" : "SMS";
  const staff = resident.staff || null;
  // iter369 — a receipts-only thread for a resident with no mobile (keyed by person, like portal chat).
  const noMobile = !!conv?.notices_only && (conv?.phone || "").startsWith("portal:");
  const notifyTyping = () => {
    if (!isWhatsApp || !convId) return;
    const now = Date.now();
    if (now - lastTypingRef.current < 18000) return;
    lastTypingRef.current = now;
    api.post(`/conversations/${convId}/typing`).catch(() => {});
  };
  const afterSend = (r) => {
    if (r?.data?.delivered) toast.success(`Sent to ${V.lc("person")}`);
    else if (r?.data?.configured === false) toast.warning(`Saved, but texting isn't connected yet — the ${V.lc("person")} won't receive this.`);
    else toast.error(`Saved, but delivery to the ${V.lc("person")} failed.`);
  };
  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      const docId = pendingDoc && reply.includes(pendingDoc.link) ? pendingDoc.id : null;
      const r = await api.post(`/conversations/${convId}/reply`, { text: reply, document_id: docId });
      setReply(""); setShowEmoji(false); setPendingDoc(null); load(); onChanged?.(); afterSend(r);
      if (r?.data?.alias_learned) toast.success(`Taught the AI: “${r.data.alias_learned}” now points to ${r.data.document_shared}`, { duration: 7000 });
    } catch (e) { toast.error("Could not send — please try again"); }
    setSending(false);
  };
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File is too large (max 5 MB)"); return; }
    setPendingFile(f);
    setShowEmoji(false); setShowCanned(false);
  };
  const sendAttachment = async (f) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", reply);
      const r = await api.post(`/conversations/${convId}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReply(""); setPendingFile(null); load(); onChanged?.(); afterSend(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send attachment"); }
    setSending(false);
  };
  const doSend = () => {
    if (sending) return;
    if (pendingFile) sendAttachment(pendingFile);
    else sendReply();
  };
  const resolve = async () => { await api.post(`/conversations/${convId}/resolve`); onChanged?.(); toast.success("Conversation resolved"); };
  const archive = async () => { await api.post(`/conversations/${convId}/archive`); onChanged?.(); toast.success("Conversation archived"); };
  const unarchive = async () => { await api.post(`/conversations/${convId}/unarchive`); load(); onChanged?.(); toast.success("Back in the inbox"); };
  const humanReason = (r) => { const s = (r || "escalated").replace(/_/g, " ").trim(); return s.charAt(0).toUpperCase() + s.slice(1); };
  const deleteConv = async () => {
    try {
      await api.delete(`/conversations/${convId}`);
      setConfirmDel(false); onChanged?.(); toast.success("Conversation deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const claim = async () => { await api.post(`/conversations/${convId}/claim`); load(); onChanged?.(); toast.success("You've taken over — the AI is paused"); };
  const release = async () => {
    const cid = convId;
    await api.post(`/conversations/${cid}/release`); load(); onChanged?.();
    toast.success("Handed back to the AI concierge", {
      action: {
        label: "Undo",
        onClick: async () => {
          try { await api.post(`/conversations/${cid}/claim`); load(); onChanged?.(); toast.success("Back with you — AI paused"); }
          catch { toast.error("Could not undo"); }
        },
      },
      duration: 8000,
    });
  };
  const applyCanned = (body) => { setReply((r) => (r ? r + " " : "") + body); setShowCanned(false); };
  const insertDocument = async (doc) => {
    try {
      const r = await api.post(`/conversations/${convId}/document-link`, { document_id: doc.id });
      const { link, title, insert_text } = r.data;
      setReply((cur) => (cur.trim() ? `${cur.trim()} ${link}` : insert_text));
      setPendingDoc({ id: doc.id, title, link });
      setShowDocs(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create a link for that document"); }
  };

  const handoff = async () => {
    const assignee = assignees.find((u) => u.user_id === handoffTo);
    if (!assignee || assigning) { if (!assignee) toast.error("Choose a manager"); return; }
    setAssigning(true);
    try {
      const r = await api.post(`/conversations/${convId}/handoff`, { assignee_id: assignee.user_id, note: handoffNote.trim() });
      const ref = r.data?.ref ? ` (${r.data.ref})` : "";
      toast.success(`Assigned to ${assignee.name}${ref} — it's now a tracked request`);
      setHandoffOpen(false); setHandoffTo(""); setHandoffNote("");
      load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    } finally { setAssigning(false); }
  };

  // iter465 — Print / Save-as-PDF: a clean, paginated record of the whole thread (browser print
  // dialog also offers "Save as PDF"). Secrets like gate codes / PINs are masked on the printout.
  const printConversation = () => {
    const personWord = resident.name || conv?.active_person_name || V.one("person");
    const msgs = (data?.messages || []).filter((m) => !m.notice && m.model_used !== "notice" && ((m.body || "").trim() || (m.media_items || []).length));
    if (!msgs.length) { toast.error("Nothing to print in this conversation yet."); return; }
    const w = window.open("", "_blank", "width=820,height=1040");
    if (!w) { toast.error("Please allow pop-ups to print this conversation."); return; }
    const communityName = dashTenant?.name || "Community";
    const logoUrl = (dashTenant?.brand?.logo && dashTenant?.id)
      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/tenant/${dashTenant.id}/logo` : "";
    const fmtDay = (iso) => { try { return new Date(iso).toLocaleString([], { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
    const first = msgs[0]?.created_at, last = msgs[msgs.length - 1]?.created_at;
    const range = first && last ? (fmtDay(first) === fmtDay(last) ? fmtDay(first) : `${fmtDay(first)} — ${fmtDay(last)}`) : "";
    const rows = msgs.map((m) => {
      const inbound = m.direction === "inbound";
      const who = escapeHtml(speakerOf(m, personWord));
      const when = escapeHtml(fmtTime(m.created_at));
      const atts = (m.media_items || []).map((it) => `<div class="att">&#128206; ${escapeHtml(it.filename || it.kind || "Attachment")}</div>`).join("");
      const body = escapeHtml(maskSecrets(m.body || "")).replace(/\n/g, "<br/>");
      return `<div class="msg ${inbound ? "in" : "out"}"><div class="meta"><span class="who">${who}</span><span class="when">${when}</span></div><div class="body">${body || '<span class="muted">(no text)</span>'}${atts}</div></div>`;
    }).join("");
    const css = `
      * { box-sizing: border-box; }
      body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1c1917; margin: 34px; }
      .doc-head { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #e7e5e4; padding-bottom: 14px; }
      .logo { height: 46px; width: 46px; object-fit: contain; border-radius: 8px; }
      .community { font-size: 18px; font-weight: 700; }
      .doctitle { font-size: 11px; color: #78716c; text-transform: uppercase; letter-spacing: .09em; margin-top: 2px; }
      .meta-grid { margin: 16px 0 22px; border-collapse: collapse; font-size: 12.5px; }
      .meta-grid td { padding: 3px 0; vertical-align: top; }
      .meta-grid td:first-child { color: #78716c; width: 128px; text-transform: uppercase; font-size: 10px; letter-spacing: .06em; padding-right: 18px; }
      .thread { display: flex; flex-direction: column; gap: 12px; }
      .msg { max-width: 82%; }
      .msg.out { align-self: flex-end; }
      .msg .meta { font-size: 10px; color: #78716c; margin-bottom: 3px; }
      .msg.out .meta { text-align: right; }
      .msg .meta .who { font-weight: 700; color: #44403c; }
      .msg .meta .when { margin-left: 8px; }
      .msg .body { font-size: 13px; line-height: 1.5; padding: 9px 12px; border-radius: 12px; page-break-inside: avoid; break-inside: avoid; }
      .msg.in .body { background: #f5f5f4; border: 1px solid #e7e5e4; border-top-left-radius: 4px; }
      .msg.out .body { background: #fee2e2; border: 1px solid #fecaca; border-top-right-radius: 4px; }
      .att { margin-top: 5px; font-size: 11.5px; color: #57534e; }
      .muted { color: #a8a29e; }
      .doc-foot { margin-top: 26px; padding-top: 12px; border-top: 1px solid #e7e5e4; font-size: 10px; color: #a8a29e; }
      @media print { body { margin: 0.6in; } }
    `;
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${escapeHtml(communityName)} — conversation with ${escapeHtml(personWord)}</title><style>${css}</style></head><body>`
      + `<header class="doc-head">${logoUrl ? `<img class="logo" src="${logoUrl}" alt=""/>` : ""}<div class="head-text"><div class="community">${escapeHtml(communityName)}</div><div class="doctitle">Conversation record</div></div></header>`
      + `<table class="meta-grid"><tbody>`
      + `<tr><td>${escapeHtml(V.one("person"))}</td><td>${escapeHtml(personWord)}${homeLabel(resident) ? ` &middot; ${escapeHtml(homeLabel(resident))}` : ""}</td></tr>`
      + `<tr><td>Contact</td><td>${escapeHtml(conv?.phone || "—")} &middot; ${escapeHtml(channelLabel)}</td></tr>`
      + `<tr><td>Date range</td><td>${escapeHtml(range || "—")}</td></tr>`
      + `<tr><td>Messages</td><td>${msgs.length}</td></tr>`
      + `</tbody></table>`
      + `<main class="thread">${rows}</main>`
      + `<footer class="doc-foot">Printed ${escapeHtml(fmtDay(new Date().toISOString()))} &middot; Gate codes, PINs and similar secrets are masked on this printout.</footer>`
      + `<script>window.addEventListener('load',function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},250);});<\/script>`
      + `</body></html>`;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Phones: identity on the first row, status + actions wrap onto a second row underneath
          (five controls beside a name never fit in 390px — the name got chopped to "Unit U…"). */}
      <div className="bg-ink text-white px-4 py-3 sm:px-5 sm:py-4 flex flex-col gap-2.5 shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-full bg-redline-500 flex items-center justify-center font-bold shrink-0">
            {(resident.name || conv?.active_person_name || "?")[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold leading-none flex items-center gap-2 min-w-0">
              <span className="truncate min-w-0">{resident.name || conv?.active_person_name || `Unknown ${V.lc("person")}`}</span>
              {staff ? (
                <span data-testid="staff-badge" title={`${staff.name || "Team member"} — ${staff.title}`}
                  className="text-[10px] font-semibold bg-white/15 text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shrink-0 capitalize"><ShieldCheck className="h-3 w-3" /> {staff.title}</span>
              ) : resident.verified ? (
                <span data-testid="verified-badge" className="text-[10px] font-semibold bg-emerald-500/25 text-emerald-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shrink-0"><BadgeCheck className="h-3 w-3" /> Verified</span>
              ) : resident.known === false ? (
                <span data-testid="unverified-badge" className="text-[10px] font-semibold bg-amber-500/25 text-amber-100 px-1.5 py-0.5 rounded-full shrink-0">Unverified</span>
              ) : null}
              {(resident.chips || []).slice(0, 3).map((c) => (
                <span key={c} data-testid="caller-chip" title="Audience chip — decides what the AI may share with this caller"
                  className="text-[10px] font-semibold bg-violet-500/30 text-violet-100 px-1.5 py-0.5 rounded-full shrink-0">{c}</span>
              ))}
            </div>
            <div className="text-xs text-sand-300 mt-0.5 truncate">{homeLabel(resident) ? `${homeLabel(resident)} · ` : ""}{noMobile ? "No mobile on file · notices by email" : `${conv?.phone} · ${channelLabel}`}</div>
          </div>
          <button data-testid="resident-details-btn" onClick={() => setShowResident((v) => !v)} title={`${V.one("person")} details`}
            className={`${HDR_ICON_BTN} shrink-0`}>
            <Info className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {conv?.status === "escalated" && (
            <span data-testid="escalation-badge" title={humanReason(conv.escalation_reason)}
              className="text-[11px] bg-amber-500/20 text-amber-200 px-2.5 py-1 rounded-full flex items-center gap-1 min-w-[7rem] max-w-[11rem] lg:max-w-[16rem]">
              <ShieldAlert className="h-3 w-3 shrink-0" /> <span className="truncate">{humanReason(conv.escalation_reason)}</span>
            </span>
          )}
          {conv?.status === "resolved" && <span className="text-[11px] bg-emerald-500/20 text-emerald-200 px-2.5 py-1 rounded-full">Resolved</span>}
          {conv?.status === "waiting" && !conv?.human_owned && (
            <span data-testid="waiting-badge" className="text-[11px] bg-sky-500/25 text-sky-100 px-2.5 py-1 rounded-full flex items-center gap-1">
              <Headset className="h-3 w-3" /> Waiting
            </span>
          )}
          {conv?.human_owned && conv?.status !== "resolved" && (
            <span data-testid="human-owned-badge" className="text-[11px] bg-redline-500/25 text-redline-100 px-2.5 py-1 rounded-full flex items-center gap-1">
              <Headset className="h-3 w-3" /> {conv.owned_by ? `${conv.owned_by}` : "Human"} · AI paused
            </span>
          )}
          {!readOnly && dashTenant?.copilot_enabled && conv?.channel !== "voice" && (
            <button data-testid="conv-copilot-draft" title="Have Copilot draft the next reply from this thread and our rules"
              onClick={() => navigate(copilotAskUrl(conversationReplyPrompt(conv, data?.messages)))}
              className={HDR_PILL_BTN}>
              <Sparkles className="h-3.5 w-3.5" /> Draft reply
            </button>
          )}
          {!readOnly && (
            <button data-testid="handoff-btn" title="Assign to a manager" onClick={() => setHandoffOpen(true)} disabled={assigning}
              className={`${HDR_PILL_BTN} disabled:opacity-50`}>
              <UserPlus className="h-3.5 w-3.5" /> Assign
            </button>
          )}
          <button data-testid="copy-transcript-btn" title="Copy the whole conversation as text (for audit / records)"
            onClick={() => copyText((data?.messages || []).filter((m) => !m.notice && (m.body || "").trim()).map((m) => transcriptLine(m, resident.name || conv?.active_person_name || V.one("person"))).join("\n"), "Transcript copied")}
            className={HDR_ICON_BTN}>
            <ClipboardList className="h-3.5 w-3.5" />
          </button>
          <button data-testid="print-conv-btn" title="Print this conversation, or save it as a PDF (for records)"
            onClick={printConversation}
            className={HDR_ICON_BTN}>
            <Printer className="h-3.5 w-3.5" />
          </button>
          {onToggleExpand && (
            <button data-testid="conv-fullscreen-btn" onClick={onToggleExpand}
              title={expanded ? "Exit full screen (Esc)" : "Open full screen"}
              className="hidden lg:inline-flex shrink-0 h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 items-center justify-center transition active:scale-90">
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          )}
          {!readOnly && (conv?.archived ? (
            <button data-testid="unarchive-conv-btn" onClick={unarchive} title="Put this conversation back in the inbox"
              className={`${HDR_PILL_BTN} shrink-0`}>
              <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
            </button>
          ) : (
            <button data-testid="archive-conv-btn" onClick={archive} title="Archive conversation"
              className={`${HDR_ICON_BTN} shrink-0`}>
              <Archive className="h-3.5 w-3.5" />
            </button>
          ))}
          {!readOnly && (confirmDel ? (
            <span className="flex items-center gap-1">
              <button data-testid="delete-conv-confirm-btn" onClick={deleteConv} title="This permanently deletes the thread and its messages"
                className="h-7 rounded-full bg-red-500/80 hover:bg-red-500 active:bg-red-600 px-2.5 text-[11px] font-semibold transition active:scale-95">
                Delete forever
              </button>
              <button data-testid="delete-conv-cancel-btn" onClick={() => setConfirmDel(false)}
                className="h-7 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 px-2.5 text-[11px] transition active:scale-95">
                Keep
              </button>
            </span>
          ) : (
            <button data-testid="delete-conv-btn" onClick={() => setConfirmDel(true)} title="Delete conversation (permanent)"
              className="shrink-0 h-7 w-7 rounded-full bg-white/10 hover:bg-red-500/70 active:bg-red-500/90 flex items-center justify-center transition active:scale-90">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>

      {showResident && (
        <div data-testid="resident-details" className="absolute right-3 sm:right-4 top-[58px] sm:top-[70px] z-30 w-[calc(100%-1.5rem)] sm:w-72 bg-white text-ink rounded-xl border border-sand-200 shadow-xl p-4 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="font-heading font-bold text-sm">{staff ? "Team member" : `${V.one("person")} details`}</div>
            <button data-testid="resident-details-close" onClick={() => setShowResident(false)} className="text-muted-foreground hover:text-ink"><X className="h-4 w-4" /></button>
          </div>
          {staff ? (
            <p data-testid="staff-details-note" className="text-xs text-ink/70 bg-sand-50 rounded-lg p-2 capitalize-first">
              {staff.name || "A teammate"} is a <span className="font-medium">{staff.title}</span> on this {V.lc("org")}'s team — not {V.a("person")} here, so there's no {V.lc("household")} to verify.
            </p>
          ) : !resident.known && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">This number isn't linked to a verified {V.lc("household")} yet.</p>}
          <div className="space-y-2 text-sm">
            {resident.name && <div className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground shrink-0" /> <span className="font-medium">{resident.name}</span></div>}
            {homeLabel(resident) && <div className="flex items-center gap-2"><Home className="h-4 w-4 text-muted-foreground shrink-0" /> {homeLabel(resident)}</div>}
            <div className="flex items-center gap-2"><MessagesSquare className="h-4 w-4 text-muted-foreground shrink-0" /> {conv?.phone} <span className="text-xs text-muted-foreground">· {channelLabel}</span></div>
            {resident.email && <div className="flex items-center gap-2 min-w-0"><Mail className="h-4 w-4 text-muted-foreground shrink-0" /> <span className="truncate">{resident.email}</span></div>}
            {resident.members?.length > 1 && <div className="flex items-start gap-2"><Users className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> <span className="text-xs">{resident.members.join(", ")}</span></div>}
            <div className="flex items-center gap-2 pt-1 border-t border-sand-100">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{resident.open_requests || 0} open request{resident.open_requests === 1 ? "" : "s"}</span>
            </div>
          </div>
        </div>
      )}

      {conv?.had_emergency && <EmergencyConvBanner tenantId={tenantId} convId={conv.id} />}

      <div ref={scrollRef} className="flex-1 p-4 space-y-3 overflow-y-auto chat-scroll bg-sand-50 min-h-0">
        {(() => {
          const msgs = data?.messages || [];
          const reopens = (conv?.reopened_ats && conv.reopened_ats.length) ? conv.reopened_ats : (conv?.reopened_at ? [conv.reopened_at] : []);
          const markerAt = {};
          reopens.forEach((ro) => { const idx = msgs.findIndex((mm) => (mm.created_at || "") >= ro); if (idx >= 0) markerAt[idx] = ro; });
          return msgs.map((m, i) => {
            const marker = markerAt[i] ? (
              <div className="flex items-center gap-3 my-1" data-testid="reopen-divider">
                <div className="flex-1 h-px bg-sand-200" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#A8A29E] flex items-center gap-1 whitespace-nowrap">
                  <RotateCcw className="h-3 w-3" /> {V.one("person")} reopened · {fmtTime(markerAt[i])}
                </span>
                <div className="flex-1 h-px bg-sand-200" />
              </div>
            ) : null;
            if (bubbles && (m.model_used === "voice-call" || (m.body || "").startsWith("📞 AI phone call"))) {
              return <Fragment key={m.id}>{marker}<VoiceCallTranscript body={m.body} createdAt={m.created_at} fmtTime={fmtTime} /></Fragment>;
            }
            if (m.model_used === "notice") {
              // iter369 — a Messages receipt: quiet line, not a bubble. The thread stays a conversation.
              const byEmail = m.notice?.channel === "email";
              return (
                <Fragment key={m.id}>
                  {marker}
                  <div data-testid={`notice-receipt-${m.id}`} className="flex items-center gap-2 my-1">
                    <div className="flex-1 h-px bg-sand-200" />
                    <span className={`text-[10px] font-semibold flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 ${m.notice?.urgent ? "text-redline-700 bg-redline-50" : "text-[#A8A29E] bg-sand-50"}`}>
                      {byEmail ? <Mail className="h-3 w-3" /> : <MessageSquareText className="h-3 w-3" />}
                      {m.body} · {fmtTime(m.created_at)}
                    </span>
                    <div className="flex-1 h-px bg-sand-200" />
                  </div>
                </Fragment>
              );
            }
            const resident = m.direction === "inbound";
            return (
              <Fragment key={m.id}>
                {marker}
                <div className={`flex ${resident ? "justify-start" : "justify-end"}`}>
              <div className="max-w-[80%]">
                <div className={`px-3.5 py-2.5 text-sm ${resident ? "bg-white border border-sand-200 text-ink rounded-2xl rounded-tl-md" : "bg-redline-500 text-white rounded-2xl rounded-tr-md"}`}>
                  {(m.media_items || []).map((it) => <MediaBubble key={it.path} item={it} onImage={setLightbox} />)}
                  {m.body}
                </div>
                {(m.sources || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1 justify-end">
                    {m.sources.map((s, i) => (
                      s.is_rule ? (
                        <span key={i} title={s.title ? `Rule from ${s.title}` : `${V.one("org")} rule`}
                          className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 flex items-center gap-1 font-semibold">
                          <Scale className="h-2.5 w-2.5" /> {s.citation || s.section || "Rule"}
                        </span>
                      ) : s.shared_by ? (
                        <span key={i} data-testid="shared-document-chip" title={`Download link sent by ${s.shared_by}`}
                          className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 flex items-center gap-1 font-semibold">
                          <FileDown className="h-2.5 w-2.5" /> Sent: {s.title}
                        </span>
                      ) : s.source === "web" && s.url ? (
                        <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid="web-source-chip" title={s.url}
                          className="text-[10px] text-sky-800 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5 flex items-center gap-1 hover:underline">
                          <Globe className="h-2.5 w-2.5" /> {s.url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]} › {s.title}
                        </a>
                      ) : (
                        <span key={i} className="text-[10px] text-redline-700 bg-redline-50 rounded px-1.5 py-0.5 flex items-center gap-1">
                          <FileText className="h-2.5 w-2.5" /> {s.title} · {s.section}
                        </span>
                      )
                    ))}
                  </div>
                )}
                {(m.sources || []).some((s) => s.is_rule && s.rule_text) && (
                  <details data-testid={`rule-audit-${m.id}`} className={`mt-1 text-[11px] ${resident ? "" : "text-right"}`}>
                    <summary className="cursor-pointer text-amber-800 font-semibold inline-flex items-center gap-1 list-none">
                      <Scale className="h-3 w-3" /> Why this answer — {(m.sources || []).filter((s) => s.is_rule && s.rule_text).length} rule{(m.sources || []).filter((s) => s.is_rule && s.rule_text).length === 1 ? "" : "s"} cited
                    </summary>
                    <div className="mt-1 space-y-1.5 text-left">
                      {(m.sources || []).filter((s) => s.is_rule && s.rule_text).map((s, i) => (
                        <div key={i} className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-ink/80">
                          <div className="font-semibold text-amber-900">{s.citation || s.section || `${V.one("org")} rule`}</div>
                          <div className="mt-0.5">{s.rule_text}</div>
                          {s.fine && <div className="text-[10px] mt-0.5 text-amber-800 font-semibold">Penalty: {s.fine}</div>}
                          {s.source_title && <div className="text-[10px] text-ink/45 mt-0.5">Source: {s.source_title}</div>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                <div className={`group/meta text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 ${resident ? "" : "justify-end"}`}>
                  {m.is_human && <><User className="h-2.5 w-2.5" /> {m.sent_by} · </>}
                  {!m.is_human && !resident && m.model_used && <>{m.model_used} · </>}
                  {fmtTime(m.created_at)}
                  {(m.body || "").trim() && (
                    <button type="button" data-testid={`copy-msg-${m.id}`} title="Copy this message" aria-label="Copy this message"
                      onClick={() => copyText(m.body, "Message copied")}
                      className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded text-ink/35 hover:text-ink hover:bg-sand-100 opacity-60 group-hover/meta:opacity-100 transition-opacity">
                      <Copy className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
              </Fragment>
            );
          });
        })()}
      </div>

      {/* Emoji picker */}
      {showEmoji && !readOnly && (
        <div data-testid="emoji-picker" className="absolute bottom-[76px] left-3 z-20 bg-white border border-sand-200 rounded-2xl shadow-xl p-3 w-72">
          <div className="grid grid-cols-8 gap-1">
            {EMOJIS.map((e) => (
              <button key={e} onClick={() => { setReply((r) => r + e); }} className="text-xl hover:bg-sand-100 rounded-lg p-1 transition-colors">{e}</button>
            ))}
          </div>
        </div>
      )}

      {showDocs && !readOnly && (
        <DocumentPicker convId={convId} onPick={insertDocument} onClose={() => setShowDocs(false)} />
      )}

      {/* Quick replies */}
      {showCanned && !readOnly && (
        <div data-testid="canned-list" className="absolute bottom-[76px] left-3 right-3 z-20 bg-white border border-sand-200 rounded-2xl shadow-xl p-2 max-h-64 overflow-y-auto">
          {canned.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 text-center">
              No quick replies yet.{" "}
              <button data-testid="canned-empty-settings-link" onClick={() => navigate("/dashboard/settings?tab=channels&section=quick-replies-section")} className="font-semibold text-redline-600 hover:underline">Add them in Settings → Channels → Quick replies</button>
            </div>
          ) : canned.map((c) => (
            <button key={c.id} data-testid={`canned-${c.id}`} onClick={() => applyCanned(c.body)}
              className="w-full text-left p-2.5 rounded-xl hover:bg-sand-50 transition-colors">
              <div className="font-semibold text-sm text-ink">{c.title}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{c.body}</div>
            </button>
          ))}
        </div>
      )}

      {!readOnly && conv?.status !== "resolved" && (
        <div className="border-t border-sand-200 bg-white shrink-0">
          <div className="flex items-center gap-2 px-3 pt-3">
            {conv?.human_owned ? (
              <button data-testid="release-conv-btn" onClick={release}
                className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-ink-700 bg-sand-100 rounded-full py-2 hover:bg-sand-200 transition-colors">
                <Bot className="h-4 w-4" /> Return to AI
              </button>
            ) : (
              <button data-testid="claim-conv-btn" onClick={claim}
                className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-ink rounded-full py-2 hover:bg-ink-700 transition-colors">
                <Headset className="h-4 w-4" /> Take over — pause AI
              </button>
            )}
            <button data-testid="resolve-conv-btn" onClick={resolve}
              className="flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-full px-4 py-2 hover:bg-emerald-100 transition-colors">
              <CheckCircle2 className="h-4 w-4" /> Resolve
            </button>
          </div>
          {pendingFile && (
            <div data-testid="pending-file" className="mx-3 mt-3 flex items-center gap-3 rounded-xl border border-sand-200 bg-sand-50 p-2.5">
              {pendingFile.type?.startsWith("image/") ? (
                <img src={URL.createObjectURL(pendingFile)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-white border border-sand-200 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-ink-700" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink truncate">{pendingFile.name}</div>
                <div className="text-xs text-muted-foreground">Ready to send — press Send</div>
              </div>
              <button data-testid="pending-file-remove" onClick={() => setPendingFile(null)} title="Remove attachment"
                className="h-8 w-8 shrink-0 rounded-full text-ink-700 hover:bg-sand-200 flex items-center justify-center transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-1.5 p-3">
            {noMobile ? (
              <div data-testid="conv-no-mobile-hint" className="w-full rounded-2xl border border-sand-200 bg-sand-50 p-3.5 text-sm text-ink/70 flex items-start gap-2.5">
                <Mail className="h-4 w-4 shrink-0 mt-0.5 text-ink/50" />
                <span>This {V.lc("person")} has no mobile on file, so they only receive notices by email. Add a phone number on the roster to text them — conversations live on the {V.lc("org")} number.</span>
              </div>
            ) : data?.reply_guard?.guard ? (
              <div data-testid="conv-reply-guard" className="w-full rounded-2xl border border-amber-300 bg-amber-50 p-3.5">
                <div className="flex items-start gap-2.5">
                  <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-amber-900">Can't text this caller</div>
                    <p className="text-sm text-amber-800 mt-0.5">{data.reply_guard.message}</p>
                    {(data.reply_guard.phone || conv?.phone) && (
                      <a data-testid="conv-callback-btn" href={`tel:${data.reply_guard.phone || conv?.phone}`}
                        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 transition-colors">
                        <Phone className="h-4 w-4" /> Call back {data.reply_guard.phone || conv?.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ) : (
            <>
            <input ref={fileRef} type="file" className="hidden" onChange={pickFile}
              accept="image/*,application/pdf,.pdf,.doc,.docx,video/*,audio/*" data-testid="attach-input" />
            <button data-testid="attach-btn" onClick={() => { setShowDocs(false); setShowCanned(false); setShowEmoji(false); fileRef.current?.click(); }} disabled={sending} title="Attach a file"
              className="h-10 w-10 shrink-0 rounded-full text-ink-700 hover:bg-sand-100 flex items-center justify-center transition-colors disabled:opacity-50">
              <Paperclip className="h-[18px] w-[18px]" />
            </button>
            <button data-testid="send-document-btn" onClick={() => { setShowDocs((v) => !v); setShowCanned(false); setShowEmoji(false); }} disabled={sending}
              title="Send a document — drop a download link into your reply"
              className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${showDocs || pendingDoc ? "bg-redline-50 text-redline-600" : "text-ink-700 hover:bg-sand-100"}`}>
              <FileDown className="h-[18px] w-[18px]" />
            </button>
            <button data-testid="canned-btn" onClick={() => { setShowCanned((v) => !v); setShowEmoji(false); setShowDocs(false); }} title="Quick replies"
              className={`hidden sm:flex h-10 w-10 shrink-0 rounded-full items-center justify-center transition-colors ${showCanned ? "bg-redline-50 text-redline-600" : "text-ink-700 hover:bg-sand-100"}`}>
              <MessageSquareText className="h-[18px] w-[18px]" />
            </button>
            <button data-testid="emoji-btn" onClick={() => { setShowEmoji((v) => !v); setShowCanned(false); setShowDocs(false); }} title="Emoji"
              className={`hidden sm:flex h-10 w-10 shrink-0 rounded-full items-center justify-center transition-colors ${showEmoji ? "bg-redline-50 text-redline-600" : "text-ink-700 hover:bg-sand-100"}`}>
              <Smile className="h-[18px] w-[18px]" />
            </button>
            {speechSupported && (
              <button data-testid="manager-reply-mic" onClick={toggleDictation} disabled={sending}
                title={listening ? "Stop dictation" : "Dictate your reply"}
                className={`relative h-10 w-10 shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${listening ? "bg-redline-500 text-white" : "text-ink-700 hover:bg-sand-100"}`}>
                {listening && <span className="absolute inset-0 rounded-full bg-redline-400/50 animate-ping" />}
                <Mic className="h-[18px] w-[18px] relative" />
              </button>
            )}
            <input data-testid="manager-reply-input" value={reply} onChange={(e) => { setReply(e.target.value); notifyTyping(); }}
              onKeyDown={(e) => e.key === "Enter" && doSend()} placeholder={listening ? "Listening…" : (pendingFile ? "Add a caption (optional)…" : pendingDoc ? `Add a note to go with ${pendingDoc.title}…` : "Reply as a human…")}
              className="flex-1 min-w-0 rounded-full bg-sand-50 border border-sand-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 focus:border-redline-300" />
            <button data-testid="manager-reply-send" onClick={doSend} disabled={sending}
              className="h-11 w-11 shrink-0 rounded-full bg-redline-500 text-white flex items-center justify-center hover:bg-redline-600 transition-colors disabled:opacity-50">
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </button>
            </>
            )}
          </div>
        </div>
      )}
      {!readOnly && conv?.status === "resolved" && (
        <div className="border-t border-sand-200 bg-white shrink-0 p-3">
          <button data-testid="reopen-conv-btn" onClick={claim}
            className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-ink rounded-full py-2.5 hover:bg-ink-700 transition-colors">
            <Headset className="h-4 w-4" /> Reopen &amp; take over
          </button>
          <p className="text-[11px] text-center text-muted-foreground mt-2">This conversation was resolved. Reopening pauses the AI and puts you back in control.</p>
        </div>
      )}
      {readOnly && <div className="p-3 text-center text-xs text-muted-foreground bg-white border-t border-sand-200 shrink-0">{V.many("board")} have read-only access</div>}

      {lightbox && (
        <div data-testid="lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-zoom-out">
          <button className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"><X className="h-5 w-5" /></button>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <Dialog open={handoffOpen} onOpenChange={(o) => { setHandoffOpen(o); if (!o) { setHandoffTo(""); setHandoffNote(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="handoff-dialog">
          <DialogHeader>
            <DialogTitle>Hand off to a manager</DialogTitle>
            <DialogDescription>This becomes a tracked request assigned to the manager, so it won't slip through the cracks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <label className="text-sm font-semibold text-ink">Assign to</label>
              {assignees.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1.5">No managers on this {V.lc("org")} yet. Add one in Team.</p>
              ) : (
                <div className="mt-2 space-y-1.5 max-h-52 overflow-y-auto">
                  {assignees.map((u) => (
                    <button key={u.user_id} type="button" data-testid={`handoff-to-${u.user_id}`} onClick={() => setHandoffTo(u.user_id)}
                      className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${handoffTo === u.user_id ? "border-redline-400 bg-redline-50" : "border-sand-200 hover:border-ink"}`}>
                      <span className="h-7 w-7 rounded-full bg-redline-50 text-redline-700 text-xs font-bold flex items-center justify-center shrink-0">{(u.name || "?")[0]}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{u.name || u.email}</span>
                        <span className="block text-[11px] text-muted-foreground capitalize">{(u.role || "").replace(/_/g, " ")}</span>
                      </span>
                      {handoffTo === u.user_id && <CheckCircle2 className="h-4 w-4 text-redline-500 shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Add a note <span className="font-normal text-muted-foreground">(optional)</span></label>
              <textarea data-testid="handoff-note" value={handoffNote} onChange={(e) => setHandoffNote(e.target.value)} rows={3}
                placeholder={`Context for the manager — e.g. '${V.one("person")} called about a burst pipe in Unit 4, needs a callback today.'`}
                className="mt-1.5 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 focus:border-redline-300 resize-none" />
              <p className="text-[11px] text-muted-foreground mt-1">The note travels with the request so context isn't lost.</p>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="handoff-cancel" onClick={() => setHandoffOpen(false)} className="rounded-full px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="handoff-confirm" onClick={handoff} disabled={assigning || !handoffTo}
              className="rounded-full bg-redline-500 text-white font-semibold px-5 py-2 text-sm hover:bg-redline-600 transition-colors disabled:opacity-50 inline-flex items-center gap-2">
              {assigning && <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />} Assign request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
